'use client';

import { InlineAlert } from '@/components/alert';
import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import ModalBox from '@/components/modalBox/ModalBox';
import { RichText } from '@/components/richText/RichText';
import { SwdCircularProgress } from '@/components/swdCircularProgress/SwdCircularProgress';
import { isStoryblokEditorContext } from '@/helper/storyblok/isStoryblokEditorContext';
import useSticky from '@/hooks/useSticky';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import type { ComponentVotingGalleryStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import classes from './VotingGallery.module.scss';
import VotingItem from './VotingItem';
import { getPollResult, saveVote } from './actions';

const VotingGallery: React.FC<{ blok: ComponentVotingGalleryStoryblok }> = ({ blok }) => {
  const path = usePathname();
  const localStorageId = `voting-gallery-${blok._uid}`;
  const [votedFor, setVotedFor] = useState<string[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [checkedHasVoted, setCheckedHasVoted] = useState(false);
  const [pollResult, setPollResult] = useState<Record<string, number>>();
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [openError, setOpenError] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { isSticky } = useSticky<HTMLDivElement | null>(actionsRef, 16);
  const setErrorModalOpen = useHeaderStore((state) => state.setErrorModalOpen);

  useEffect(() => {
    // Checks if user has already voted
    setHasVoted(Boolean(localStorage?.getItem(localStorageId)));

    // Load voted images for from localStorage
    setVotedFor((prev) =>
      localStorage.getItem(localStorageId) ? JSON.parse(localStorage.getItem(localStorageId)!) : prev
    );

    // Mark that we have checked the hasVoted status to avoid initial flicker
    // Defer to next macrotask to ensure tests can assert pre-effect state
    const t = setTimeout(() => setCheckedHasVoted(true));
    return () => clearTimeout(t);
  }, []);

  // Load poll result if in Storyblok UI or public voting enabled
  useEffect(() => {
    const shouldFetch = isStoryblokEditorContext() || Boolean(blok.isPublicVoting);
    if (shouldFetch) {
      const fetchPollResult = async () => {
        try {
          setPollResult(await getPollResult(blok.votingId));
        } catch (error) {
          console.error('Error fetching poll result:', error);
        }
      };
      fetchPollResult();
    }
  }, [hasVoted, blok._uid, blok.isPublicVoting]);

  // Open the global ServiceErrorModal after render when an error occurred during submit
  useEffect(() => {
    if (openError) {
      setErrorModalOpen(true);
    }
  }, [openError, setErrorModalOpen]);

  // Submit the vote
  const submitVote = async () => {
    if (typeof localStorageId === 'undefined') {
      return;
    }
    setSubmitting(true);
    const response = await saveVote(votedFor, blok, path);
    if (response.success) {
      localStorage.setItem(localStorageId, JSON.stringify(votedFor));
      setHasVoted(true);
      setSuccessOpen(true);
    } else {
      setOpenError(true);
    }
    setSubmitting(false);
  };
  return (
    <div id={blok.anchor || undefined} {...storyblokEditable(blok)} className={classes.blok}>
      <ModalBox
        open={successOpen}
        large={true}
        heading={blok.votedTitle}
        buttonText={'Schließen'}
        toggleOpen={() => setSuccessOpen(false)}
        closeButtonAlign={'left'}
      >
        <RichText text={blok.votedText} />
      </ModalBox>

      <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
        {blok.title}
      </HeadlineField>

      <ul className={classNames(classes.items, blok.columns ? (classes as any)[`columns-${blok.columns}`] : undefined)}>
        {blok.items?.map((item) => (
          <li key={item._uid}>
            <VotingItem
              item={item}
              blok={blok}
              hasVoted={hasVoted}
              votedFor={votedFor}
              setVotedFor={setVotedFor}
              voteCount={typeof pollResult !== 'undefined' ? pollResult[item._uid] : undefined}
            />
          </li>
        ))}
      </ul>
      {checkedHasVoted && !hasVoted && (
        <div
          className={classNames(classes.submitWrapper, { [classes.submitWrapperSticky!]: isSticky })}
          ref={actionsRef}
        >
          <p role="status" aria-live="polite" aria-atomic="true" className="font-display-7">
            {(() => {
              const max = Number.parseInt(String(blok.maxVotes ?? ''), 10);
              const isMaxValid = Number.isFinite(max);
              const votesLeft = isMaxValid ? Math.max(0, max - votedFor.length) : '';
              return blok.selectionText
                .replace('%%COUNT%%', votedFor.length.toString())
                .replace('%%MAX%%', isMaxValid ? String(max) : '')
                .replace('%%VOTES_LEFT%%', isMaxValid ? String(votesLeft) : '');
            })()}
          </p>
          <Button
            data-testid="voting-submit-vote-button"
            disabled={votedFor.length === 0}
            className={classes.submit}
            variant="ctaFull"
            onClick={submitVote}
          >
            {blok.submitLabel}
            {submitting && <SwdCircularProgress size={16} />}
          </Button>
        </div>
      )}

      {hasVoted && (
        <InlineAlert className={classes.alertFullWidth} severity="success">
          {blok.votedAlertTitle && <h6 className={classes.alertTitle}>{blok.votedAlertTitle}</h6>}
          <RichText text={blok.votedAlertText} className="font-copy" />
          <div className={classes.alertCta}>
            <Button variant="ctaFull" onClick={() => setSuccessOpen(true)}>
              {blok.votedAlertCTAButtonText}
            </Button>
          </div>
        </InlineAlert>
      )}
    </div>
  );
};

export default VotingGallery;
